import React, { useState, useEffect } from 'react';

interface PlatformIntegration {
    id: string;
    name: string;
    type: string;
    base_url: string;
    auth_type: string;
    created_at: string;
    configured: boolean;
}

const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === "development") {
        return "http://localhost:8000";
    }
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return "http://localhost:8000";
    }
    const apiHost = hostname.replace('frontend-alpha', 'orchestrator');
    return `https://${apiHost}`;
};

const API_BASE_URL = getApiBaseUrl();

const PlatformIntegrations: React.FC = () => {
    const [integrations, setIntegrations] = useState<PlatformIntegration[]>([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        type: 'jira',
        url: '',
        authentication: {
            type: 'basic',
            username: '',
            password: '',
            token: ''
        }
    });

    useEffect(() => {
        fetchIntegrations();
    }, []);

    const fetchIntegrations = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/platform-integrations/list`);
            if (response.ok) {
                const data = await response.json();
                setIntegrations(data.integrations || []);
            }
        } catch (error) {
            console.error('Failed to fetch platform integrations:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveIntegration = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/platform-integrations`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                setShowAddModal(false);
                fetchIntegrations();
                // Reset form
                setFormData({
                    name: '',
                    type: 'jira',
                    url: '',
                    authentication: { type: 'basic', username: '', password: '', token: '' }
                });
            } else {
                alert('Failed to save integration');
            }
        } catch (error) {
            console.error('Error saving integration:', error);
            alert('Error saving integration');
        }
    };

    const handleDeleteIntegration = async (id: string, name: string) => {
        if (!window.confirm(`Delete integration "${name}"?`)) return;
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/endpoints/${id}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                fetchIntegrations();
            }
        } catch (error) {
            console.error('Error deleting integration:', error);
        }
    };

    const getPlatformIcon = (type: string) => {
        const icons: { [key: string]: string } = {
            jira: '🎯',
            confluence: '📝',
            database: '🗄️',
            databricks: '📊',
            bitbucket: '🔀',
            github: '🐙',
            gitlab: '🦊',
            slack: '💬',
            teams: '👥'
        };
        return icons[type] || '🔗';
    };

    if (loading) return <div>Loading platform integrations...</div>;

    return (
        <div style={{ padding: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2>Platform Integrations</h2>
                <button 
                    className="widget-button button-primary"
                    onClick={() => setShowAddModal(true)}
                >
                    + Add Integration
                </button>
            </div>

            <p style={{ color: '#666', marginBottom: '2rem' }}>
                Connect to external platforms for enhanced testing capabilities. These integrations enable features like defect tracking, documentation export, and data synchronization.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
                {integrations.map(integration => (
                    <div key={integration.id} style={{
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '1rem',
                        backgroundColor: '#f9f9f9'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>{getPlatformIcon(integration.type)}</span>
                                {integration.name}
                            </h3>
                            <span style={{
                                padding: '0.2rem 0.5rem',
                                backgroundColor: integration.configured ? '#28a745' : '#ffc107',
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '0.8rem'
                            }}>
                                {integration.configured ? 'Active' : 'Pending'}
                            </span>
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                            Type: {integration.type.toUpperCase()}
                        </div>
                        <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                            {integration.base_url}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button className="widget-button button-secondary" style={{ fontSize: '0.8rem' }}>
                                Test Connection
                            </button>
                            <button 
                                className="widget-button"
                                style={{ fontSize: '0.8rem', backgroundColor: '#dc3545', color: 'white' }}
                                onClick={() => handleDeleteIntegration(integration.id, integration.name)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Add Integration Modal */}
            {showAddModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.9)',
                        padding: '2rem',
                        borderRadius: '8px',
                        width: '500px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        color: '#333 !important'
                    }}>
                        <h3>Add Platform Integration</h3>
                        
                        <div style={{ marginBottom: '1rem' }}>
                            <label>Integration Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                                placeholder="e.g., Company JIRA"
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label>Platform Type</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({...formData, type: e.target.value})}
                                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                            >
                                <option value="jira">JIRA</option>
                                <option value="confluence">Confluence</option>
                                <option value="database">Database</option>
                                <option value="databricks">Databricks</option>
                                <option value="bitbucket">Bitbucket</option>
                                <option value="github">GitHub</option>
                                <option value="gitlab">GitLab</option>
                                <option value="slack">Slack</option>
                                <option value="teams">Teams</option>
                            </select>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label>URL</label>
                            <input
                                type="url"
                                value={formData.url}
                                onChange={(e) => setFormData({...formData, url: e.target.value})}
                                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                                placeholder="https://your-instance.atlassian.net"
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label>Authentication Type</label>
                            <select
                                value={formData.authentication.type}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    authentication: {...formData.authentication, type: e.target.value}
                                })}
                                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                            >
                                <option value="basic">Basic Auth</option>
                                <option value="token">API Token</option>
                                <option value="oauth">OAuth</option>
                            </select>
                        </div>

                        {formData.authentication.type === 'basic' && (
                            <>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label>Username</label>
                                    <input
                                        type="text"
                                        value={formData.authentication.username}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            authentication: {...formData.authentication, username: e.target.value}
                                        })}
                                        style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                                    />
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={formData.authentication.password}
                                        onChange={(e) => setFormData({
                                            ...formData,
                                            authentication: {...formData.authentication, password: e.target.value}
                                        })}
                                        style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                                    />
                                </div>
                            </>
                        )}

                        {formData.authentication.type === 'token' && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label>API Token</label>
                                <input
                                    type="password"
                                    value={formData.authentication.token}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        authentication: {...formData.authentication, token: e.target.value}
                                    })}
                                    style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                                />
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button 
                                className="widget-button button-secondary"
                                onClick={() => setShowAddModal(false)}
                            >
                                Cancel
                            </button>
                            <button 
                                className="widget-button button-primary"
                                onClick={handleSaveIntegration}
                            >
                                Save Integration
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlatformIntegrations;